// 장비 장착 피드백(2026-10-28 대표님 「장착이 정확하게 바뀌었는지 인지가 안 돼서 헤맨다」).
// 장착이 일어난 틱마다 ① 화면 위쪽 가장자리(가운데 위, 플레이 영역 밖)에 「무기 장착: 이름 공격 +N ▲」 토스트
// ② 장착한 칸·상세 카드의 번쩍임과 「장착됨」 배지(gearPanel이 equipMarks를 읽는다) ③ 퀘스트 줄의 「✔ 장착 완료」(equipTick)를 낸다.
// 「추천 장비 모두 장착」 신호(EQUIP_BEST_EVENT)를 받으면 더 좋은 장비를 부위별로 한 번에 장착한다.
// 화면 가운데는 가리지 않는다. 애니메이션은 CSS·WAAPI 한 번씩(프레임 루프 JS 없음), 객체는 장착이 일어날 때만 만든다.
import type { EquipSlot } from '../shared/content/types';
import { SLOT_ORDER } from '../shared/gearLedger';
import type { GameCommand, GameView } from '../shared/types';
import { el } from './dom';
import { gearCard } from './gearModel';
import type { EquipUpgrade } from './growthModel';
import { makeRequestId } from './rpgModel';
import {
  EQUIP_BEST_EVENT, EQUIP_FX, EquipChangeTracker, equipDelta, equipHints, equipMarks, equipTick, equipToast, scaleEquipFx,
} from './equipFeedbackModel';
import { reducedMotion } from './skillbar';

const STYLE_ID = 'dh-equip-fx-style';
const CSS =
  '@keyframes dh-eq-flash{0%{box-shadow:0 0 0 0 rgba(125,255,160,.95),0 0 0 rgba(125,255,160,0)}35%{box-shadow:0 0 0 3px rgba(125,255,160,.95),0 0 22px rgba(125,255,160,.9)}100%{box-shadow:0 0 0 0 rgba(125,255,160,0),0 0 0 rgba(125,255,160,0)}}' +
  '@keyframes dh-eq-badge{0%{opacity:0;transform:translateY(6px) scale(.7)}12%{opacity:1;transform:none}80%{opacity:1}100%{opacity:0}}' +
  '@keyframes dh-eq-check{0%{transform:scale(.2) rotate(-30deg);opacity:0}55%{transform:scale(1.35) rotate(6deg);opacity:1}100%{transform:scale(1) rotate(0);opacity:1}}' +
  '.dh-g-tile.just,.dh-g-card.just{animation:dh-eq-flash calc(.9s * var(--dh-eq-k,1)) ease-out 2}' +
  '.dh-eq-badge{position:absolute;left:0;right:0;margin:0 auto;width:fit-content;max-width:96%;bottom:3px;padding:1px 5px;border-radius:9px;background:#1f8f4e;border:1px solid #9bffbb;color:#fff;font:900 10px system-ui,"Malgun Gothic",sans-serif;white-space:nowrap;pointer-events:none;animation:dh-eq-badge calc(' + EQUIP_FX.markMs + 'ms * var(--dh-eq-k,1)) ease-out forwards}' +
  '.dh-g-card .dh-eq-badge{position:static;margin:0;align-self:flex-start;font-size:12px;padding:2px 9px}' +
  '.dh-eq-check{display:inline-block;color:#7dffa0;animation:dh-eq-check .45s cubic-bezier(.2,1.3,.4,1) both}' +
  '@media (prefers-reduced-motion:reduce){.dh-g-tile.just,.dh-g-card.just,.dh-eq-check{animation:none}.dh-eq-badge{animation:none}}';

export function installEquipFxStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID; st.textContent = CSS;
  document.head.appendChild(st);
}

const UP = '#7dffa0', DOWN = '#ff8a94';

export class EquipFeedback {
  private readonly toast: HTMLDivElement;
  /** 장착 판정(처음·영웅 전환 프레임은 알리지 않음, 새 장착 성공 결과만) */
  private readonly tracker = new EquipChangeTracker();
  private upgrades: readonly EquipUpgrade[] = [];
  private view: GameView | null = null;
  private anim: Animation | null = null;
  private hideTimer = 0;
  /** QA: 지금까지 낸 토스트 수와 마지막 문구 */
  shown = 0;
  lastText = '';

  constructor(parent: HTMLElement, private readonly send: (c: GameCommand) => void) {
    installEquipFxStyle();
    try { const q = new URLSearchParams(location.search); if (q.has('qa')) scaleEquipFx(Number(q.get('eqfx'))); } catch { /* 주소를 못 읽어도 정상 시간 */ }
    this.toast = el('div', 'position:absolute;left:50%;transform:translateX(-50%);top:64px;width:max-content;max-width:min(90vw,420px);box-sizing:border-box;display:none;z-index:45;pointer-events:none;padding:7px 12px;border-radius:10px;' +
      'background:rgba(10,18,34,.88);border:1.5px solid #5fe08e;box-shadow:0 4px 14px rgba(0,0,0,.5);color:#f4f8ff;font:800 14px/1.35 system-ui,"Malgun Gothic",sans-serif;', parent);
    this.toast.dataset.testid = 'equip-toast';
    this.toast.setAttribute('role', 'status');
    this.toast.setAttribute('aria-live', 'polite');
    window.addEventListener(EQUIP_BEST_EVENT, () => this.equipBest());
  }

  /** 추천 장비를 부위별로 모두 장착(성장 목표가 고른 더 좋은 장비). 결과 토스트는 장착이 반영된 프레임에 update가 낸다 */
  equipBest(): void {
    if (this.upgrades.length === 0) return;
    // 장착은 가방 창이 열린 동안에만 받는다(게임 규칙). 퀘스트 줄에서 눌렀으면 잠깐 열었다 닫아, 마을 화면에서 바로 토스트·체크가 보이게 한다
    const wasOpen = this.view?.village.inventoryOpen === true;
    if (!wasOpen) this.send({ type: 'openInventory' });
    for (const u of this.upgrades) this.send({ type: 'gearEquip', uid: u.uid, requestId: makeRequestId() });
    if (!wasOpen) this.send({ type: 'closeMenu' });
  }

  /** 프레임마다: 장착 칸 변화를 비교만 한다(같으면 할당 없음) */
  update(v: GameView, upgrades: readonly EquipUpgrade[]): void {
    this.upgrades = upgrades; this.view = v;
    equipHints.count = upgrades.length;
    const g = v.gear;
    if (!g) return;
    const t = g.totals;
    const ch = this.tracker.observe(v.heroId ?? 'kael', g.last, SLOT_ORDER, g.equipped, t);
    if (!ch) return;
    const cards = ch.idx.map((i) => { const it = g.items.find((x) => x.uid === g.equipped[SLOT_ORDER[i]]); return it ? gearCard(it, true, v.heroId ?? 'kael') : null; }).filter((c): c is NonNullable<typeof c> => !!c);
    if (cards.length === 0) return;
    const parts = equipDelta(ch.prev, t);
    const at = performance.now();
    equipMarks.mark(cards.map((c) => c.uid), at);
    const text = equipToast(cards.map((c) => ({ slot: c.slotName, name: c.name })), parts);
    this.showToast(text.head, parts, text.none);
    equipTick.until = at + EQUIP_FX.tickMs; equipTick.name = cards.length === 1 ? cards[0].name : cards.length + '개 장비';
    equipTick.detail = parts.length ? parts.map((p) => p.text + (p.up ? ' ▲' : ' ▼')).join(' · ') : '능력치는 그대로';
  }

  private showToast(head: string, parts: readonly { text: string; up: boolean }[], none: string): void {
    const box = this.toast;
    box.textContent = '';
    el('span', 'color:#7dffa0;margin-right:6px;', box, '✔');
    el('span', '', box, head + ' ');
    if (parts.length === 0) el('span', 'color:#cfd8f5;', box, none);
    parts.forEach((p, i) => {
      el('span', 'color:' + (p.up ? UP : DOWN) + ';font-weight:900;', box, p.text + (p.up ? ' ▲' : ' ▼') + (i < parts.length - 1 ? ',' : ''));
      if (i < parts.length - 1) box.appendChild(document.createTextNode(' '));
    });
    box.style.display = 'block';
    this.shown++; this.lastText = box.textContent ?? '';
    window.clearTimeout(this.hideTimer);
    try { this.anim?.cancel(); } catch { /* 이미 끝남 */ }
    this.anim = reducedMotion() ? null : box.animate([{ opacity: 0, transform: 'translate(-50%,-14px)' }, { opacity: 1, transform: 'translate(-50%,0)' }], { duration: 220, easing: 'ease-out' });
    this.hideTimer = window.setTimeout(() => {
      this.anim = box.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, fill: 'forwards' });
      this.anim.onfinish = () => { box.style.display = 'none'; };
    }, EQUIP_FX.toastMs);
  }
}
